/**
 * The two dialogs a representative starts work from (DG.33).
 *
 * Both replace something that used to be on the panel: an inline field for asking, and a hidden file
 * input the header clicked for sending. The panel is now a record of what has happened - requested,
 * received, sent, and the trail - and nothing on it starts anything.
 *
 * ## Both stage before they commit
 *
 * A rep listing what they need from somebody thinks in threes, and picking four files one dialog-open at
 * a time would be worse than the field this took away. So each dialog collects a list and hands it over
 * once. Neither knows how to talk to the API: they call back with an array and `ExchangeView` owns what
 * happens next, including what a half-committed batch looks like.
 *
 * ## The send dialog is also the fix for a defect
 *
 * A file picker opens only under transient user activation. The header used to call `click()` on a hidden
 * input, which works for a rep's own press and can never work for a host's: a `message` handler carries no
 * activation and `postMessage` does not transfer any, so a CRM's button posting `iqz-ddds:action` produced
 * nothing at all, silently. Here the picker is opened by a control the rep clicks in this document, which
 * is where the activation is. There was no way to keep the old shape and fix that.
 */
import { useCallback, useRef, useState } from 'react';
import { Modal } from '../Modal';
import { Button, Row } from '../primitives';
import { formatBytes } from '../upload/tus';

/** Shared by both: which shape the container wants, and how to get out. */
interface DialogChrome {
  readonly onClose: () => void;
  readonly variant?: 'overlay' | 'takeover';
}

/** "Send this document" against "Send 3 documents", for either verb. */
function commitLabel(verb: string, count: number): string {
  return count === 1 ? `${verb} this document` : `${verb} ${count} documents`;
}

/** Everything but the item at `index`. */
function without<T>(items: readonly T[], index: number): readonly T[] {
  return items.filter((_, at) => at !== index);
}

/** One staged item, with the control that takes it off the list again. */
function StagedRow({
  name,
  meta,
  onRemove,
}: {
  readonly name: string;
  readonly meta?: string;
  readonly onRemove: () => void;
}): React.JSX.Element {
  return (
    <Row
      name={name}
      {...(meta === undefined ? {} : { meta })}
      action={
        <Button variant="quiet" size="small" label={`Remove ${name}`} onClick={onRemove}>
          Remove
        </Button>
      }
    />
  );
}

import { RichTextEditor, getPlainTextLength } from '../RichTextEditor';

export interface RequestDocumentsDialogProps extends DialogChrome {
  /** Called once with the formatted document request. The dialog does not close itself. */
  readonly onSubmit: (label: string) => void;
  /** Maximum length for the formatted request. Defaults to 2000. */
  readonly documentRequestMaxLength?: number | undefined;
}

/**
 * Asking for several documents in one pass.
 *
 * The hint is the same sentence the inline field carried and moves with it, because it is the one thing
 * about this field a rep has to know: the prospect reads the label verbatim, in an email, so it is the
 * rep's own words and never a code.
 */
export function RequestDocumentsDialog({
  onSubmit,
  onClose,
  variant,
  documentRequestMaxLength = 2000,
}: RequestDocumentsDialogProps): React.JSX.Element {
  const [request, setRequest] = useState('');
  const requestTextLength = getPlainTextLength(request);
  const isRequestOverLimit = requestTextLength > documentRequestMaxLength;

  const handleCommit = useCallback(() => {
    if (requestTextLength === 0 || isRequestOverLimit) return;
    onSubmit(request);
  }, [isRequestOverLimit, onSubmit, request, requestTextLength]);

  return (
    <Modal
      title="Ask for documents"
      onClose={onClose}
      {...(variant === undefined ? {} : { variant })}
      footer={
        <>
          <Button variant="quiet" width="auto" onClick={onClose}>
            Cancel
          </Button>
          <Button width="auto" disabled={requestTextLength === 0 || isRequestOverLimit} onClick={handleCommit}>
            Request documents
          </Button>
        </>
      }
    >
      <RichTextEditor
        label="Ask for a document"
        hint="Describe the documents you need. Supports formatting and hyperlinks."
        value={request}
        onChange={setRequest}
        maxLength={documentRequestMaxLength}
        placeholder="Describe the documents you need..."
      />
    </Modal>
  );
}

export interface SendDocumentsDialogProps extends DialogChrome {
  /** Called once, with every file in the order it was picked. The dialog does not close itself. */
  readonly onSubmit: (files: readonly File[]) => void;
}

/**
 * Choosing several documents to send.
 *
 * Dropping and picking both land in the same list, and the list is what makes this worth a dialog rather
 * than a bare picker: sizes are on the screen before anything moves, so a rep who picked the 300MB scan
 * instead of the 300KB form can see it, and one wrong file can be removed without starting again.
 */
export function SendDocumentsDialog({ onSubmit, onClose, variant }: SendDocumentsDialogProps): React.JSX.Element {
  const [files, setFiles] = useState<readonly File[]>([]);
  const [over, setOver] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  /**
   * Add what arrived, minus anything already staged.
   *
   * Name and size together. A rep who picks the same file twice should not queue it twice, and two
   * genuinely different scans out of two folders often share a name - deduplicating on the name alone
   * would silently drop one of them.
   */
  const stage = useCallback((incoming: FileList | null) => {
    if (incoming === null) return;
    setFiles((current) => {
      const seen = new Set(current.map((file) => `${file.name}:${String(file.size)}`));
      const fresh = [...incoming].filter((file) => !seen.has(`${file.name}:${String(file.size)}`));
      return [...current, ...fresh];
    });
  }, []);

  const remove = useCallback((index: number) => setFiles((current) => without(current, index)), []);

  return (
    <Modal
      title="Send documents"
      onClose={onClose}
      {...(variant === undefined ? {} : { variant })}
      footer={
        <>
          <Button variant="quiet" width="auto" onClick={onClose}>
            Cancel
          </Button>
          <Button width="auto" disabled={files.length === 0} onClick={() => onSubmit(files)}>
            {commitLabel('Send', Math.max(files.length, 1))}
          </Button>
        </>
      }
    >
      {/*
        The drop zone is the button, rather than a div with a button inside it.

        A drop target has to be operable without a pointer, and the honest way to get that is a native
        control instead of a role and a keyboard handler bolted onto a div. So the whole zone is one
        button: clicking or pressing it opens the picker, and dragging onto it does the same job for
        anybody who prefers that. It also keeps the property this dialog exists for - the click that
        reaches the file input is a real one, in this document, carrying the user activation a browser
        requires before it will show a file chooser.
      */}
      <button
        type="button"
        className={`ddds-drop${over ? ' ddds-drop--over' : ''}`}
        onClick={() => picker.current?.click()}
        onDragOver={(event) => {
          // Both halves are required: without the default prevented the browser navigates to the file.
          event.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          stage(event.dataTransfer.files);
        }}
      >
        <span className="ddds-drop__lead">Choose files</span>{' '}
        <span className="ddds-drop__hint">or drop them here</span>
      </button>

      {/* Off-screen rather than `display: none`, which some browsers refuse to click programmatically. */}
      <input
        ref={picker}
        type="file"
        multiple
        style={{ position: 'absolute', left: '-9999px', width: 1, height: 1 }}
        onChange={(event) => {
          stage(event.target.files);
          // Reset, so a file removed by mistake can be picked again without restarting the dialog.
          event.target.value = '';
        }}
        aria-hidden="true"
        tabIndex={-1}
      />

      {files.length > 0 && (
        <ul className="ddds-list">
          {files.map((file, index) => (
            <StagedRow
              key={`${file.name}:${String(file.size)}`}
              name={file.name}
              meta={formatBytes(file.size)}
              onRemove={() => remove(index)}
            />
          ))}
        </ul>
      )}
    </Modal>
  );
}
